// Who can take a booking that has no therapist — the Assign view (Cal-02/03).
//
// A therapist can take it when all four hold, checked in this order so the
// reason shown is the first that fails: not on a day off, qualified for the
// service, on shift for the whole session, and not in another session that
// overlaps it. Same hub first.
//
// ⚠ OPEN (before this is wired to a real assign): the design lists free
// therapists from OTHER hubs too ("Other hub · Pasig"). Whether a therapist may
// take a booking outside her own hub is not a recorded rule — it is shown here
// as the design draws it, for review, and is a question for Luigi before the
// real action exists (never-invent-policy.md). Travel time is not considered.

import { type BoardRow, type CalendarDay, type SessionBlock, spanLabel } from "./calendar-board";

export interface AssignCandidate {
  row: BoardRow;
  sameHub: boolean;
}

export interface AssignBlocked {
  row: BoardRow;
  reason: string;
}

export interface AssignOptions {
  session: SessionBlock;
  candidates: AssignCandidate[];
  blocked: AssignBlocked[];
}

function blockedReason(row: BoardRow, session: SessionBlock): string | null {
  if (row.dayOff || !row.shift) return "Day off";
  if (row.live === "sos") return "Has an open SOS";
  if (!row.skills.includes(session.serviceName)) return `Doesn't do ${session.serviceName}`;
  if (row.shift.startMin > session.startMin || row.shift.endMin < session.endMin) {
    return `Shift ${spanLabel(row.shift)}`;
  }
  const clash = row.blocks.find(
    (block) =>
      block.kind === "session" &&
      block.bookingId !== session.bookingId &&
      block.startMin < session.endMin &&
      block.endMin > session.startMin,
  );
  return clash ? `Busy ${spanLabel(clash)}` : null;
}

export function assignOptions(day: CalendarDay, bookingId: string): AssignOptions | null {
  const session = day.sessions.find((entry) => entry.bookingId === bookingId);
  if (!session) return null;
  const candidates: AssignCandidate[] = [];
  const blocked: AssignBlocked[] = [];
  for (const row of day.rows) {
    if (row.role !== "therapist") continue;
    const reason = blockedReason(row, session);
    if (reason) blocked.push({ row, reason });
    else candidates.push({ row, sameHub: row.hubId === session.hubId });
  }
  candidates.sort(
    (a, b) => Number(b.sameHub) - Number(a.sameHub) || a.row.name.localeCompare(b.row.name),
  );
  return { session, candidates, blocked };
}
